import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { Button, Form, message, Modal, Steps } from "antd";
import { ProjectContext } from "context/ProjectProvider";
import { useLocation } from "react-router";
import { IntegrationStep } from "./steps/integration/index";
import ScheduleStep from "./steps/schedule";
import WelcomeWindow from "./steps/welcome-window";
import WelcomeLoadingPage from "./loading-page";
import SummaryStep from "./steps/summary";

import "./welcome-startup-setup.scss";
import { TSummary } from "./model";

function WelcomeStartupSetup() {
  const { state } = useLocation() as { state: any };
  const { projectDetails, isFeatureFlagEnabled } = useContext(ProjectContext);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTourStarted, setIsTourStarted] = useState(false);
  const [isProgressMode, setProgressMode] = useState(false);

  const [current, setCurrent] = useState(0);

  const [summary, setSummary] = useState<TSummary>();

  const progressTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const [integrationForm] = Form.useForm();
  const [importScheduleForm] = Form.useForm();
  const [buttonEnable, setButtonEnable] = useState(false);

  const isSmartSetupEnabled = isFeatureFlagEnabled("SMART_LINKING");

  const steps = useMemo(
    () => [
      {
        key: "IntegrationStep",
        title: "Project Data",
        content: (
          <IntegrationStep
            integrationForm={integrationForm}
            setButtonEnable={setButtonEnable}
          />
        ),
        action: "Next"
      },
      {
        key: "ScheduleStep",
        title: "Schedule",
        content: (
          <ScheduleStep
            importScheduleForm={importScheduleForm}
            setButtonEnable={setButtonEnable}
          />
        ),
        action: "Start Setup"
      },
      {
        key: "SmartSetup",
        title: "Smart Setup",
        content: (
          <SummaryStep summary={summary} projectName={projectDetails?.name} />
        ),
        action: "Finish"
      }
    ],
    [importScheduleForm, integrationForm, projectDetails?.name, summary]
  );

  // const prev = () => {
  //   if (current > 0) setCurrent(current - 1);
  // };

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = () => {
    // setIsModalOpen(false);
  };
  const next = useCallback(() => {
    // next() is only ever called inside an `if (current < steps.length - 1)` guard
    // in the button onClick, so the false branch here is never reachable.
    /* v8 ignore next */
    if (current < steps.length - 1) setCurrent(current + 1);
  }, [current, steps.length]);

  const handleCancel = () => {
    // setIsModalOpen(false);
  };

  useEffect(() => {
    if (isSmartSetupEnabled && state?.isShowWelcomeWindow) showModal();
  }, [isSmartSetupEnabled, state?.isShowWelcomeWindow]);

  useEffect(
    () => () => {
      if (progressTimerRef.current) clearTimeout(progressTimerRef.current);
    },
    []
  );

  const tourPage = useMemo(
    () => (
      <div className="w-full h-[623px] p-6 relative">
        <div className="steps-content w-full h-[560px]">
          {steps[current].content}
        </div>
        <div className="sticky bottom-0 flex w-full items-center space-x-4">
          <Steps
            current={current}
            size="small"
            items={steps.map((item) => ({
              key: item.title,
              title: item.title
            }))}
          />
          <Button
            type="primary"
            disabled={!buttonEnable}
            className="!bg-ciq-blue-700 rounded border-none w-[150px] hover:!bg-ciq-blue-700 "
            data-testid="index-button-action-welcome-setup"
            onClick={async () => {
              if (current < steps.length - 1) {
                try {
                  if (current === 0) {
                    await integrationForm.validateFields();
                    integrationForm.submit();
                    setButtonEnable(false);
                  }
                  if (current === 1) {
                    await importScheduleForm.validateFields();
                    importScheduleForm.submit();
                    setProgressMode(true);
                  }
                  next();
                } catch (err) {
                  console.error(err);
                }
              } else {
                message.success("Processing complete!");
                setIsModalOpen(false);

                window.location.assign(
                  `/project/${summary?.project_id}/dashboard`
                );
              }
            }}
          >
            {steps[current].action}
          </Button>
        </div>
      </div>
    ),
    [
      buttonEnable,
      current,
      importScheduleForm,
      integrationForm,
      next,
      steps,
      summary?.project_id
    ]
  );

  const currentView = useMemo(() => {
    if (isProgressMode)
      return (
        <WelcomeLoadingPage
          onProcessCompleted={(sum) => {
            setSummary(sum);

            // onProcessCompleted can fire more than once; only the first
            // completion schedules the exit from progress mode.
            if (progressTimerRef.current) return;
            progressTimerRef.current = setTimeout(() => {
              progressTimerRef.current = undefined;
              setProgressMode(false);
            }, 500);
          }}
        />
      );

    return isTourStarted ? (
      tourPage
    ) : (
      <WelcomeWindow
        onGetStarted={() => {
          setIsTourStarted(true);
        }}
      />
    );
  }, [isProgressMode, isTourStarted, tourPage]);

  return (
    <Modal
      open={isModalOpen}
      onOk={handleOk}
      onCancel={handleCancel}
      footer={null}
      closable={false}
      centered
      width={1003}
      className="welcome-startup-setup"
      data-testid="index-modal-welcome-setup"
    >
      <div className="w-full h-[630px]">{currentView}</div>
    </Modal>
  );
}

export default WelcomeStartupSetup;
